import type { GenerationResult } from '../types';
import { toJsonLdString, toMicrodata, toRdfa } from './format-converters';

const BRAND_LINE = 'Generated by The SEO Consultant.ai · Schema Markup Generator';
const BRAND_URL = 'https://theseoconsultant.ai';

function nowIso(): string {
  return new Date().toISOString();
}

function nowReadable(): string {
  return new Date().toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

/**
 * File-naming scheme:
 *   schema-<host>-<types>-<date>.<ext>
 *   schema-<host>-graph-<date>.<ext>   (when there are 2+ entities)
 */
export function suggestedFilename(
  generations: GenerationResult[],
  ext: string
): string {
  const date = new Date().toISOString().slice(0, 10);
  const sourceUrl = generations[0]?.sourceUrl;
  const types = generations.map((g) => g.type).join('-').toLowerCase();
  const tag = generations.length > 1 ? 'graph' : types || 'schema';

  let host = '';
  if (sourceUrl) {
    try {
      host = new URL(sourceUrl).hostname.replace(/^www\./, '').replace(/[^a-z0-9]+/gi, '-');
    } catch {
      // ignore
    }
  }
  const slug = [host, tag].filter(Boolean).join('-').slice(0, 80).toLowerCase();
  return `schema-${slug}-${date}.${ext}`;
}

// ---------------------------------------------------------------------------
// Format-specific exports — graph param is the combined object from buildGraph()
// ---------------------------------------------------------------------------

export function exportJsonLdScript(graph: Record<string, unknown>): string {
  return toJsonLdString(graph, { script: true });
}

export function exportJsonLdRaw(graph: Record<string, unknown>): string {
  return toJsonLdString(graph, { script: false });
}

export function exportMicrodata(graph: Record<string, unknown>): string {
  return toMicrodata(graph);
}

export function exportRdfa(graph: Record<string, unknown>): string {
  return toRdfa(graph);
}

// ---------------------------------------------------------------------------
// Bundled report
// ---------------------------------------------------------------------------

export function exportMarkdownReport(
  generations: GenerationResult[],
  graph: Record<string, unknown>
): string {
  const lines: string[] = [];
  const types = generations.map((g) => g.type).join(' + ');
  const sourceUrl = generations[0]?.sourceUrl;

  lines.push(`# Schema markup: ${types}`);
  lines.push('');
  if (sourceUrl) lines.push(`Source: ${sourceUrl}`);
  lines.push(`*${BRAND_LINE} · ${nowReadable()}*`);
  lines.push('');

  lines.push('## JSON-LD');
  lines.push('');
  lines.push('```html');
  lines.push(exportJsonLdScript(graph));
  lines.push('```');
  lines.push('');

  lines.push('## Microdata');
  lines.push('');
  lines.push('```html');
  lines.push(exportMicrodata(graph));
  lines.push('```');
  lines.push('');

  lines.push('## RDFa');
  lines.push('');
  lines.push('```html');
  lines.push(exportRdfa(graph));
  lines.push('```');
  lines.push('');

  for (const g of generations) {
    if (g.warnings.length > 0) {
      lines.push(`## Before publishing: ${g.type}`);
      for (const w of g.warnings) lines.push(`- ${w}`);
      lines.push('');
    }
  }

  for (const g of generations) {
    if (g.validation.issues.length > 0) {
      lines.push(`## Validation: ${g.type}`);
      lines.push('');
      lines.push('| Severity | Field | Message |');
      lines.push('|---|---|---|');
      for (const v of g.validation.issues) {
        const msg = v.message.replace(/\|/g, '\\|');
        lines.push(`| ${v.severity} | \`${v.field}\` | ${msg} |`);
      }
      lines.push('');
    }
  }

  for (const g of generations) {
    if (g.fieldNotes.length > 0) {
      lines.push(`## Field notes: ${g.type}`);
      lines.push('');
      lines.push('| Field | Status | Note |');
      lines.push('|---|---|---|');
      for (const n of g.fieldNotes) {
        const note = (n.note || '').replace(/\|/g, '\\|');
        lines.push(`| \`${n.field}\` | ${n.status} | ${note} |`);
      }
      lines.push('');
    }
  }

  lines.push('---');
  lines.push(`*${BRAND_LINE} — [${BRAND_URL}](${BRAND_URL})*`);
  lines.push('');
  return lines.join('\n');
}

export function exportJsonReport(generations: GenerationResult[]): string {
  return JSON.stringify(
    {
      _meta: {
        tool: 'Schema Markup Generator',
        brandUrl: BRAND_URL,
        exportedAt: nowIso(),
      },
      generations,
    },
    null,
    2
  );
}

// ---------------------------------------------------------------------------
// Browser helpers
// ---------------------------------------------------------------------------

export function triggerDownload(filename: string, mimeType: string, content: string): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
